/**
 * Closest-value matching for everything CRX reads (user, 2026-10-13: "look
 * for similarity if it doesn't find the exact name and suggest it … show
 * something like predicted"). Code only, on this computer.
 *
 * Words are normalised first — security-trade abbreviations (CPO, CP, EP,
 * PSD, B6), British/American spellings, common Arabic terms — then compared
 * word by word with room for plurals, prefixes and one-letter typos. A match
 * is EXACT when the option's words appear as written in the text, otherwise
 * it is a prediction the person confirms.
 */

/** Phrases that mean the same thing, rewritten to one form before comparing. */
const SYNONYMS: Array<[RegExp, string]> = [
    [/\bc\.?\s?p\.?\s?o\.?s?\b/gi, " close protection officer "],
    [/\bbody\s?guards?\b/gi, " close protection officer "],
    [/\bp\.?s\.?d\.?\b/gi, " protective security detail "],
    [/\bclose\s+protection\b|\bexecutive\s+protection\b|\bpersonal\s+protection\b|\bvip\s+protection\b/gi, " protection "],
    [/\bc\.?p\.?\b|\be\.?p\.?\b/gi, " protection "],
    [/\barmou?red\b|\barmou?r\b|\bballistic\b|\bb[4-7]\b|\bbr[4-7]\b/gi, " armoured "],
    [/\bsoft[\s-]?skin\b|\bnon[\s-]?armou?red\b|\bunarmou?red\b/gi, " softskin "],
    [/\bland\s?cruiser\b|\bsuburban\b|\bsuvs?\b|\b4x4\b/gi, " suv "],
    [/\bsedans?\b|\bsaloons?\b|\bs[\s-]?class\b|\bs\s?580\b/gi, " sedan "],
    [/\bchauffeurs?\b|\bdrivers?\b/gi, " driver "],
    [/\bmeet\s*(?:&|and|n)\s*(?:assist|greet)\b|\bairport\s+(?:pickup|reception|transfer)\b/gi, " meet assist airport "],
    [/\bsnr\.?\b|\bsr\.?\b|\blead\b|\bteam\s+leader\b/gi, " senior "],
    [/\bjnr\.?\b|\bjr\.?\b/gi, " junior "],
    [/\bmid(?:[\s-]?level)?\b/gi, " intermediate "],
    [/\bu\.?a\.?e\.?\b|\bemirates\b/gi, " united arab emirates "],
    [/\bk\.?s\.?a\.?\b/gi, " saudi arabia "],
    [/\bdxb\b/gi, " dubai airport "],
    // Arabic
    [/حراس?ة?\s*شخصي(?:ة|ون|ين)?/g, " close protection officer "],
    [/حماية\s*شخصية|حماية/g, " protection "],
    [/حارس|حراس/g, " guard "],
    [/مسلح(?:ة|ون|ين)?/g, " armed "],
    [/غير\s*مسلح(?:ة|ون|ين)?/g, " unarmed "],
    [/مصفح(?:ة|تان|تين|ات)?/g, " armoured "],
    [/سيار(?:ة|ات|تين|تان)/g, " vehicle "],
    [/سائق(?:ين|ون)?/g, " driver "],
    [/فاخر(?:ة)?/g, " luxury sedan "],
    [/استقبال|توصيل(?:ة)?/g, " meet assist "],
    [/مطار/g, " airport "],
    [/\bأول\b|أول|كبير/g, " senior "],
    [/متوسط/g, " intermediate "],
    [/مبتدئ|مستجد/g, " junior "],
    [/نقل\s*آمن|نقل/g, " transport "],
    [/دبي/g, " dubai "], [/أبوظبي|ابوظبي/g, " abu dhabi "], [/الرياض/g, " riyadh "], [/جدة/g, " jeddah "], [/عمّان|عمان/g, " amman "], [/رام\s*الله/g, " ramallah "],
    [/الإمارات|الامارات/g, " united arab emirates "], [/السعودية/g, " saudi arabia "], [/الأردن|الاردن/g, " jordan "], [/فلسطين/g, " palestine "],
];

/** Words that never decide a match. */
const STOPWORDS = new Set(["a", "an", "the", "and", "or", "with", "for", "per", "of", "to", "in", "on", "at", "by", "from", "plus", "incl", "including", "excl", "excluding", "vat", "aed", "usd", "sar", "eur", "jod", "ils", "rate", "rates", "price", "day", "days", "hour", "hours", "hr", "hrs", "full", "each", "co", "llc", "ltd", "l", "c", "fze", "company", "group"]);

/** Text in the comparable form: lower case, no accents or punctuation, synonyms rewritten. */
export const normalizeTerm = (text: string): string => {
    let out = ` ${String(text ?? "")} `;
    for (const [pattern, replacement] of SYNONYMS) out = out.replace(pattern, replacement);
    return out.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").replace(/\s+/g, " ").trim();
};

const stem = (word: string): string => (word.length > 4 && word.endsWith("s") && !word.endsWith("ss") ? word.slice(0, -1) : word);

/** The words that count, normalised. */
export const termWords = (text: string): string[] => normalizeTerm(text).split(" ").map(stem).filter((word) => word.length > 1 && !STOPWORDS.has(word));

const oneEditApart = (a: string, b: string): boolean => {
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) { i++; j++; continue; }
        if (++edits > 1) return false;
        if (a.length > b.length) i++;
        else if (b.length > a.length) j++;
        else if (a[i + 1] === b[j] && a[i] === b[j + 1]) { i += 2; j += 2; } // swapped letters
        else { i++; j++; }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
};

/** Whether two words are the same word: equal, one a ≥4-letter start of the other, or one typo apart (5+ letters). */
const sameWord = (a: string, b: string): boolean =>
    a === b
    || (Math.min(a.length, b.length) >= 4 && (a.startsWith(b) || b.startsWith(a)))
    || (Math.min(a.length, b.length) >= 5 && oneEditApart(a, b));

/**
 * How well an option fits a text, 0–1: the share of the option's words found
 * in the text, blended with the overlap both ways.
 */
export const fitScore = (text: string, option: string): number => {
    const textWords = termWords(text);
    const optionWords = termWords(option);
    if (!textWords.length || !optionWords.length) return 0;
    const found = optionWords.filter((word) => textWords.some((other) => sameWord(word, other))).length;
    const coverage = found / optionWords.length;
    const overlap = (2 * found) / (optionWords.length + textWords.length);
    return Math.round((0.7 * coverage + 0.3 * overlap) * 1000) / 1000;
};

/** Whether the option's words appear in the text as written (an exact, not a predicted, match). */
export const isLiteralMatch = (text: string, option: string): boolean => {
    const plain = (value: string) => ` ${String(value ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim()} `;
    const haystack = plain(text);
    const parts = String(option ?? "").split(/›|>|,/).map((part) => part.trim()).filter(Boolean);
    // "Dubai" on the list is "Dubai, United Arab Emirates"; "Armed" under "Executive Protection › Armed" needs both
    const first = parts[0] ?? "";
    return haystack.includes(plain(option)) || (option.includes(",") && !!first && haystack.includes(plain(first)))
        || (parts.length > 1 && !option.includes(",") && parts.every((part) => haystack.includes(plain(part))));
};

export interface FuzzyHit {
    value: string;
    score: number;
    /** The option is written in the text; false = the closest match, to show as predicted. */
    exact: boolean;
}

/**
 * The option that best fits the text, or null when none fits well enough or
 * two fit equally well.
 * @param text - What was read ("Senior Armed CPO, Dubai — 85 AED per hour").
 * @param options - The labels to choose from.
 * @param min - The lowest score that still counts (0–1).
 */
export const bestMatch = (text: string, options: string[], min = 0.5): FuzzyHit | null => {
    if (!String(text ?? "").trim() || !options.length) return null;
    // a "(new)" marker on a label (a catalogue service the record doesn't offer yet) isn't part of its name
    const nameOf = (option: string) => option.replace(/\s*\(new\)\s*$/i, "");
    const literal = options.filter((option) => isLiteralMatch(text, nameOf(option)));
    if (literal.length) {
        // the longest literal option wins ("Senior Armed" over "Armed")
        const value = [...literal].sort((a, b) => termWords(nameOf(b)).length - termWords(nameOf(a)).length)[0];
        return { value, score: 1, exact: true };
    }
    // "City, Country" options: the city decides (every place shares its country)
    const scored = options
        .map((option) => ({ option, score: option.includes(",") ? fitScore(text, option.split(",")[0]) : fitScore(text, nameOf(option)) }))
        .filter((hit) => hit.score >= min)
        .sort((a, b) => b.score - a.score);
    if (!scored.length) return null;
    if (scored.length > 1 && scored[0].score - scored[1].score < 0.05) return null;
    return { value: scored[0].option, score: scored[0].score, exact: false };
};
